import { ref } from "vue";
import { isAxiosError } from "axios";
import { Usuario } from "../usuarios/usuario";
import { AuthService, MeResponse } from "./auth-service";
import api from "@/lib/api";
import { trackAction } from "@/services/screen-recorder/track";

export enum AuthState {
    LOADING,
    UNAUTHORIZED,
    AUTHENTICATING,
    AUTHENTICATED
}

export const auth_state = ref<AuthState>(AuthState.LOADING)
export const current_user = ref<Usuario | null>()
export const impersonating = ref(false)
export const impersonator_nome = ref<string | null>(null)

export const auth_service = new AuthService(api)

function clearImpersonationFlags() {
    impersonating.value = false
    impersonator_nome.value = null
}

function applyMeResponse(user: MeResponse) {
    current_user.value = user
    impersonating.value = !!user.impersonating
    impersonator_nome.value = user.impersonator_nome ?? null
}

function markUnauthorized() {
    current_user.value = null
    clearImpersonationFlags()
    auth_state.value = AuthState.UNAUTHORIZED
}

// App.vue swaps to LoginLayout when UNAUTHORIZED; without this, mid-session 401s leave the UI authenticated.
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (isAxiosError(error) && error.response?.status === 401) {
            const url = error.config?.url ?? ''
            if (!url.includes('auth/signin')) {
                markUnauthorized()
            }
        }
        return Promise.reject(error)
    },
)

export const refreshAuthState = async () => {
    const user = await auth_service.me()
    if (!user) {
        markUnauthorized()
        return
    }
    applyMeResponse(user)
    auth_state.value = AuthState.AUTHENTICATED
}

export const singIn = async (email: string, password: string) => {
    const user = await auth_service.signIn(email, password);
    current_user.value = user
    clearImpersonationFlags()
    auth_state.value = AuthState.AUTHENTICATED
    trackAction('login', { matricula: user.matricula })
}

export const impersonate = async (id: string) => {
    const master_nome = current_user.value?.nome ?? null
    const user = await auth_service.impersonate(id);
    current_user.value = user
    impersonating.value = true
    impersonator_nome.value = master_nome
    auth_state.value = AuthState.AUTHENTICATED
    trackAction('login', { matricula: user.matricula })
}

export const stopImpersonate = async () => {
    const user = await auth_service.stopImpersonate()
    current_user.value = user
    clearImpersonationFlags()
    auth_state.value = AuthState.AUTHENTICATED
}

export const signOut = async () => {
    try {
        await auth_service.signOut()
    } finally {
        markUnauthorized()
    }
}


refreshAuthState()

